import { authenticatedRequest, getStoredToken } from './authApi';
import { REST_API_ORIGIN } from '../platform/endpoints';
import { localizedValue } from '../localization/presentationLanguage';
const savedText = (es, en) => localizedValue({ es, en });

const API_BASE = REST_API_ORIGIN;
const SAVED_SYNASTRY_BASE = `${API_BASE}/api/saved-synastries`;

function getTokenOrThrow() {
  const token = typeof getStoredToken === 'function' ? getStoredToken() : '';
  if (!token) {
    const error = new Error(savedText('No autenticado. Iniciá sesión para guardar lecturas.', 'You are not authenticated. Log in to save readings.'));
    error.code = 'UNAUTHENTICATED';
    throw error;
  }
  return token;
}

async function parseJsonSafe(response) {
  const text = await response.text();
  if (!text) return {};

  try {
    return JSON.parse(text);
  } catch {
    return {
      ok: false,
      error: savedText('Respuesta inválida del servidor', 'Invalid server response'),
      raw: text,
    };
  }
}

async function request(path = '', options = {}) {
  const token = getTokenOrThrow();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 15000);

  try {
    const response = await authenticatedRequest(`${SAVED_SYNASTRY_BASE}${path}`, {
      rawResponse: true,
      ...options,
      signal: controller.signal,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
      diagSource: 'savedSynastryApi',
    });

    const payload = await parseJsonSafe(response);

    const payloadCode = String(payload?.code || '').trim();
    const normalizedCode = payloadCode === 'SAVED_SYN_ASTRY_LIMIT_REACHED'
      ? 'LIBRARY_LIMIT_REACHED'
      : payloadCode;

    if (normalizedCode === 'LIBRARY_LIMIT_REACHED') {
      const error = new Error(
        savedText(payload?.message || 'Tu biblioteca está llena. Eliminá una lectura guardada para poder guardar una nueva.', 'Your library is full. Delete a saved reading before saving a new one.')
      );
      error.code = 'LIBRARY_LIMIT_REACHED';
      error.status = response.status;
      error.payload = payload;
      throw error;
    }

    // Only treat 409 as success when it is a real duplicate save.
    if (
      response.status === 409 &&
      (
        normalizedCode === 'DUPLICATE_SAVED_SYNASTRY' ||
        normalizedCode === 'SAVED_SYNASTRY_DUPLICATE' ||
        /duplic/i.test(String(payload?.error || '')) ||
        /duplic/i.test(String(payload?.message || '')) ||
        /already exists/i.test(String(payload?.error || '')) ||
        /already exists/i.test(String(payload?.message || ''))
      )
    ) {
      return {
        ok: true,
        duplicate: true,
        ...(payload || {}),
      };
    }

    if (!response.ok || payload?.ok === false) {
      const error = new Error(
        savedText(payload?.message || payload?.error || 'No se pudo procesar la sinastría guardada', 'The saved synastry could not be processed.')
      );
      error.code = normalizedCode || 'SAVED_SYNASTRY_REQUEST_FAILED';
      error.status = response.status;
      error.payload = payload;
      throw error;
    }

    return payload;
  } finally {
    clearTimeout(timeoutId);
  }
}

function unwrapSavedSynastryPayload(payload) {
  if (!payload || typeof payload !== 'object') return payload;
  return payload?.saved || payload?.item || payload;
}

function normalizeSavedSynastryListResponse(payload) {
  if (!payload || typeof payload !== 'object') {
    return { ok: true, items: [] };
  }

  const rawItems = Array.isArray(payload?.items)
    ? payload.items
    : Array.isArray(payload?.saved)
      ? payload.saved
      : Array.isArray(payload)
        ? payload
        : [];

  return {
    ...payload,
    items: rawItems.map((item) => unwrapSavedSynastryPayload(item)).filter(Boolean),
  };
}

function normalizePerson(person = {}, fallbackNickname = 'Persona') {
  return {
    nickname: String(person?.nickname || fallbackNickname).trim() || fallbackNickname,
    sign: String(person?.sign || person?.sunSign || person?.zodiacSign || '').trim(),
    sunSign: String(person?.sunSign || person?.sign || person?.zodiacSign || '').trim(),
    zodiacSign: String(person?.zodiacSign || person?.sunSign || person?.sign || '').trim(),
    solarSign: String(person?.solarSign || person?.sunSign || person?.sign || '').trim(),
    birthDate: String(person?.birthDate || '').trim(),
    birthTime: String(person?.birthTime || '').trim(),
    birthCity: String(person?.birthCity || '').trim(),
    birthCountry: String(person?.birthCountry || '').trim(),
    birthPlaceLabel: String(person?.birthPlaceLabel || '').trim(),
    lat:
      person?.lat === '' || person?.lat == null
        ? undefined
        : (Number.isFinite(Number(person.lat)) ? Number(person.lat) : undefined),
    lng:
      person?.lng === '' || person?.lng == null
        ? undefined
        : (Number.isFinite(Number(person.lng)) ? Number(person.lng) : undefined),
    timezoneOffsetMinutes:
      person?.timezoneOffsetMinutes === '' || person?.timezoneOffsetMinutes == null
        ? undefined
        : (Number.isFinite(Number(person.timezoneOffsetMinutes))
            ? Number(person.timezoneOffsetMinutes)
            : undefined),
  };
}

function buildDefaultTitle(personA = {}, personB = {}) {
  const a = String(personA?.nickname || 'Persona A').trim();
  const b = String(personB?.nickname || 'Persona B').trim();
  return `${a} ✦ ${b}`;
}

function buildBasePayload({ title, personA, personB, result, mode = 'deep', source = 'deep-synastry' }) {
  return {
    title: String(title || '').trim() || buildDefaultTitle(personA, personB),
    personA: normalizePerson(personA, 'Persona A'),
    personB: normalizePerson(personB, 'Persona B'),
    result,
    resultSnapshot: result,
    reading: result,
    mode,
    calculationMode: mode,
    readingType: mode,
    source,
    compatibilityType: mode === 'quick' ? 'solar' : 'deep',
    quickMode: mode === 'quick',
  };
}

export async function saveDeepSynastry({ title, personA, personB, result }) {
  if (!result || typeof result !== 'object') {
    throw new Error(savedText('Falta el resultado de la sinastría profunda', 'The deep-synastry result is missing.'));
  }

  const payload = buildBasePayload({
    title,
    personA,
    personB,
    result,
    mode: 'deep',
    source: 'deep-synastry',
  });

  const response = await request('', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

  const normalized = unwrapSavedSynastryPayload(response);
  return {
    ...(response && typeof response === 'object' ? response : {}),
    ...(normalized && typeof normalized === 'object' ? { saved: normalized, item: normalized } : {}),
  };
}

export async function saveQuickSynastry({ title, personA, personB, result }) {
  if (!result || typeof result !== 'object') {
    throw new Error('Falta el resultado de la compatibilidad solar');
  }

  const payload = buildBasePayload({
    title,
    personA,
    personB,
    result: {
      ...result,
      mode: 'quick',
      source: 'quick-solar',
      compatibilityType: 'solar',
      pair: {
        ...(result?.pair || {}),
        signA: String(personA?.sunSign || personA?.sign || result?.pair?.signA || '').trim(),
        signB: String(personB?.sunSign || personB?.sign || result?.pair?.signB || '').trim(),
      },
      personA: {
        ...(result?.personA || {}),
        nickname: String(personA?.nickname || 'Persona A').trim(),
        sign: String(personA?.sign || personA?.sunSign || '').trim(),
        sunSign: String(personA?.sunSign || personA?.sign || '').trim(),
        zodiacSign: String(personA?.zodiacSign || personA?.sunSign || personA?.sign || '').trim(),
      },
      personB: {
        ...(result?.personB || {}),
        nickname: String(personB?.nickname || 'Persona B').trim(),
        sign: String(personB?.sign || personB?.sunSign || '').trim(),
        sunSign: String(personB?.sunSign || personB?.sign || '').trim(),
        zodiacSign: String(personB?.zodiacSign || personB?.sunSign || personB?.sign || '').trim(),
      },
    },
    mode: 'quick',
    source: 'quick-solar',
  });

  const response = await request('', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

  const normalized = unwrapSavedSynastryPayload(response);
  return {
    ...(response && typeof response === 'object' ? response : {}),
    ...(normalized && typeof normalized === 'object' ? { saved: normalized, item: normalized } : {}),
  };
}

export async function listSavedSynastries(limit = 20) {
  const safeLimit = Number.isFinite(Number(limit))
    ? Math.max(1, Math.min(50, Math.trunc(Number(limit))))
    : 20;

  const response = await request(`?limit=${safeLimit}`, {
    method: 'GET',
  });

  return normalizeSavedSynastryListResponse(response);
}

export async function getSavedSynastryById(id) {
  const safeId = String(id || '').trim();
  if (!safeId) {
    throw new Error(savedText('Falta el id de la sinastría guardada', 'The saved-synastry ID is missing.'));
  }

  const response = await request(`/${encodeURIComponent(safeId)}`, {
    method: 'GET',
  });

  const normalized = unwrapSavedSynastryPayload(response);
  return {
    ...(response && typeof response === 'object' ? response : {}),
    ...(normalized && typeof normalized === 'object' ? { item: normalized, saved: normalized } : {}),
  };
}

export async function deleteSavedSynastry(id) {
  const safeId = String(id || '').trim();
  if (!safeId) {
    throw new Error(savedText('Falta el id de la sinastría guardada', 'The saved-synastry ID is missing.'));
  }

  return request(`/${encodeURIComponent(safeId)}`, {
    method: 'DELETE',
  });
}

const savedSynastryApi = {
  saveDeepSynastry,
  saveQuickSynastry,
  listSavedSynastries,
  getSavedSynastryById,
  deleteSavedSynastry,
};

export default savedSynastryApi;
